
import { computed, defineComponent, onMounted } from "vue";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import timezone from "dayjs/plugin/timezone";
import { storeToRefs } from "pinia";
import { useVaultStore } from "@/stores/vault";
dayjs.extend(timezone);
dayjs.extend(advancedFormat);

export default defineComponent({
  name: "vault-transactions",
  setup() {
    const vault = useVaultStore();
    const {
      transactions,
      isTransactionsLoading,
      summaryBtcPrice,
      transactionsUpdatedAt,
      transactionsPage,
      transactionsPageSize,
      transactionsHasMore,
      transactionsTotalCount,
    } = storeToRefs(vault);

    const formattedTransactionsUpdated = computed(() => {
      const updated = transactionsUpdatedAt?.value;
      if (!updated) return "";
      return formatDateTimezone(updated);
    });

    const totalPages = computed(() => {
      const total = transactionsTotalCount.value || 0;
      const size = transactionsPageSize.value || 1;
      return Math.max(1, Math.ceil(total / size));
    });

    const maxPage = computed(() => totalPages.value - 1);

    const windowStart = computed(() => Math.max(0, transactionsPage.value - 2));
    const windowEnd = computed(() =>
      Math.min(maxPage.value, transactionsPage.value + 2)
    );

    const pageWindow = computed(() => {
      const pages: number[] = [];
      for (let p = windowStart.value; p <= windowEnd.value; p += 1) {
        pages.push(p);
      }
      return pages;
    });

    const fetchTransactions = () => vault.fetchTransactions();

    const transactionDirectionClass = (direction: string) => {
      switch (direction) {
        case "incoming":
          return "fas fa-sign-in-alt text-success";
        case "outgoing":
          return "fas fa-sign-out-alt text-danger";
        default:
          return "fas fa-question-circle";
      }
    };

    const transactionSymbolIconClass = (direction: string) => {
      switch (direction) {
        case "outgoing":
          return "fas fa-minus text-danger fa-xs align-middle";
        case "incoming":
          return "fas fa-plus text-success fa-xs align-middle";
        default:
          return "fas fa-coins";
      }
    };

    const showLeftEllipsis = computed(() => windowStart.value > 0);
    const showRightEllipsis = computed(() => windowEnd.value < maxPage.value);

    const transactionStatusClass = (status: string) => {
      switch (status) {
        case "confirmed":
          return "badge bg-success";
        case "completed":
          return "badge bg-success";
        case "pending":
          return "badge bg-warning";
        default:
          return "badge bg-secondary";
      }
    };

    const transactionsStatusLabel = (status: string) => {
      switch (status) {
        case "confirmed":
          return "Confirmada";
        case "completed":
          return "Completada";
        case "pending":
          return "Pendiente";
        default:
          return "Desconocido";
      }
    };

    const formatDateTimezone = (date: number | Date) =>
      dayjs(date).format("YYYY/MM/DD HH:mm:ss z");

    const formatUSD = (amountBtc: string | number) => {
      const btc =
        typeof amountBtc === "number" ? amountBtc : parseFloat(amountBtc);
      const usdValue = btc * (summaryBtcPrice.value || 0);
      return usdValue.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    };

    onMounted(() => {
      fetchTransactions();
    });

    const goToPage = (page: number) => {
      if (isTransactionsLoading.value) return;
      const max = maxPage.value;
      const target = Math.min(Math.max(0, page), max);
      vault.fetchTransactions({ page: target });
    };

    const nextTransactionsPage = () => {
      goToPage(transactionsPage.value + 1);
    };

    const prevTransactionsPage = () => {
      goToPage(transactionsPage.value - 1);
    };

    return {
      transactions,
      isTransactionsLoading,
      summaryBtcPrice,
      transactionsUpdatedAt,
      transactionsPage,
      transactionsPageSize,
      transactionsHasMore,
      transactionsTotalCount,
      totalPages,
      pageWindow,
      showLeftEllipsis,
      showRightEllipsis,
      formattedTransactionsUpdated,
      fetchTransactions,
      goToPage,
      nextTransactionsPage,
      prevTransactionsPage,
      transactionDirectionClass,
      transactionSymbolIconClass,
      transactionStatusClass,
      transactionsStatusLabel,
      formatDateTimezone,
      formatUSD,
    };
  },
});
